import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import { FaUserPlus, FaUserCheck, FaPlus, FaCopy } from "react-icons/fa";
import { toast } from "react-toastify";
import BatchService from "../../../../services/batchService";
import StudentService from "../../../../services/studentService";

const AddStudentModal = ({
  show,
  onHide,
  batchId,
  availableStudents,
  onStudentAdded,
}) => {
  const [addMethod, setAddMethod] = useState("existing"); // 'existing' or 'new'
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [newStudent, setNewStudent] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdStudentId, setCreatedStudentId] = useState(null);

  useEffect(() => {
    if (!show) {
      resetForm();
    }
  }, [show]);

  const resetForm = () => {
    setAddMethod("existing");
    setSelectedStudentId("");
    setNewStudent({ name: "", email: "", phone: "" });
    setGeneratedPassword("");
    setShowSuccess(false);
    setCreatedStudentId(null);
  };

  const handleAddExistingStudent = async () => {
    if (!selectedStudentId) {
      toast.error("Please select a student");
      return;
    }

    try {
      setLoading(true);
      await BatchService.addStudentToBatch(batchId, selectedStudentId);
      toast.success("Student added to batch successfully");
      onStudentAdded();
      onHide();
      resetForm();
    } catch (error) {
      console.error("Failed to add student:", error);
      toast.error(error.response?.data?.message || "Failed to add student");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewStudent = async () => {
    if (!newStudent.name.trim()) {
      toast.error("Student name is required");
      return;
    }
    if (!newStudent.email.trim()) {
      toast.error("Email is required");
      return;
    }
    if (!newStudent.phone.trim()) {
      toast.error("Phone number is required");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newStudent.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      setLoading(true);

      // Step 1: Create the student (using your existing createStudentOnly method)
      const createResponse = await StudentService.createStudentOnly({
        name: newStudent.name,
        email: newStudent.email,
        phone: newStudent.phone,
      });

      const studentId = createResponse.data?.student_id;
      const generatedPwd = createResponse.data?.password;

      if (!studentId) {
        throw new Error("Failed to create student");
      }

      // Step 2: Add student to batch
      await BatchService.addStudentToBatch(batchId, studentId);

      setGeneratedPassword(generatedPwd);
      setCreatedStudentId(studentId);
      setShowSuccess(true);
    } catch (error) {
      console.error("Failed to create and add student:", error);
      toast.error(error.response?.data?.message || "Failed to add student");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (addMethod === "existing") {
      handleAddExistingStudent();
    } else {
      handleAddNewStudent();
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  // Generate preview password based on name
  const getPreviewPassword = () => {
    return "123456";
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        onHide();
        resetForm();
      }}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FaUserPlus className="me-2 text-primary" />
          Add Student to Batch
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!showSuccess ? (
          <>
            {/* Method Selection */}
            <div className="mb-4">
              <Form.Label className="fw-bold">Add Student Method</Form.Label>
              <div className="d-flex gap-3">
                <Form.Check
                  type="radio"
                  label="Add Existing Student"
                  name="addMethod"
                  value="existing"
                  checked={addMethod === "existing"}
                  onChange={(e) => setAddMethod(e.target.value)}
                />
                <Form.Check
                  type="radio"
                  label="Add New Student"
                  name="addMethod"
                  value="new"
                  checked={addMethod === "new"}
                  onChange={(e) => setAddMethod(e.target.value)}
                />
              </div>
            </div>

            {addMethod === "existing" ? (
              // Existing Student Selection
              <Form.Group className="mb-3">
                <Form.Label>Select Student</Form.Label>
                <Form.Select
                  value={selectedStudentId}
                  onChange={(e) => setSelectedStudentId(e.target.value)}
                >
                  <option value="">-- Select a student --</option>
                  {availableStudents &&
                    availableStudents.map((student) => (
                      <option key={student.id} value={student.id}>
                        {student.name} ({student.email})
                      </option>
                    ))}
                </Form.Select>
                {(!availableStudents || availableStudents.length === 0) && (
                  <Form.Text className="text-warning">
                    No available students. All students are already in this
                    batch.
                  </Form.Text>
                )}
              </Form.Group>
            ) : (
              // New Student Form
              <>
                <Alert variant="info" className="mb-3">
                  <strong>ℹ️ Note:</strong> New student will be automatically:
                  <ul className="mb-0 mt-2">
                    <li>✓ Created with status "Approved"</li>
                    <li>✓ Added to this batch</li>
                    <li>✓ Enrolled in the course</li>
                    <li>✓ Payment tracking created</li>
                  </ul>
                </Alert>

                <Form.Group className="mb-3">
                  <Form.Label>Full Name *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter student's full name"
                    value={newStudent.name}
                    onChange={(e) =>
                      setNewStudent({ ...newStudent, name: e.target.value })
                    }
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Email Address *</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter student's email"
                    value={newStudent.email}
                    onChange={(e) =>
                      setNewStudent({ ...newStudent, email: e.target.value })
                    }
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Phone Number *</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="Enter student's phone number"
                    value={newStudent.phone}
                    onChange={(e) =>
                      setNewStudent({ ...newStudent, phone: e.target.value })
                    }
                  />
                </Form.Group>

                <Alert variant="secondary" className="mb-0">
                  <small>
                    <strong>🔐 Auto-generated Password:</strong>{" "}
                    {getPreviewPassword()}
                    <br />
                    <em className="text-muted">
                      Student can change password after first login.
                    </em>
                  </small>
                </Alert>
              </>
            )}
          </>
        ) : (
          // Success Message with Generated Password
          <div className="text-center">
            <div className="mb-3">
              <div className="display-1 text-success">✓</div>
              <h4 className="mt-2">Student Added Successfully!</h4>
            </div>

            <Alert variant="success" className="text-start">
              <strong>Student Details:</strong>
              <br />
              Name: {newStudent.name}
              <br />
              Email: {newStudent.email}
              <br />
              Phone: {newStudent.phone}
            </Alert>

            <Alert variant="warning" className="text-start">
              <strong>⚠️ Important - Login Credentials:</strong>
              <br />
              <div className="d-flex justify-content-between align-items-center">
                <code className="fw-bold fs-5">{generatedPassword}</code>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => copyToClipboard(generatedPassword)}
                >
                  <FaCopy className="me-1" /> Copy
                </Button>
              </div>
              <hr />
              <small>
                Please save this password and share it with the student. The
                student can change it after first login.
              </small>
            </Alert>

            <Button
              variant="primary"
              onClick={() => {
                onStudentAdded();
                onHide();
                resetForm();
              }}
            >
              Close
            </Button>
          </div>
        )}
      </Modal.Body>

      {!showSuccess && (
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              onHide();
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={
              loading ||
              (addMethod === "existing" && !selectedStudentId) ||
              (addMethod === "new" &&
                (!newStudent.name || !newStudent.email || !newStudent.phone))
            }
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              <>
                <FaUserPlus className="me-2" />
                {addMethod === "existing" ? "Add to Batch" : "Create & Add"}
              </>
            )}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default AddStudentModal;
